import '../Table.scss';
import React, { useEffect } from 'react';
import { ColumnDef, flexRender } from '@tanstack/react-table';
import { Card } from '@design-system/components/Card/Card';
import { OrderBy, useMutationTableVM } from './useMutationTableVM';
import { Typography } from '@design-system/components/Typography/Typography';
import { Button } from '@design-system/components/Buttons/Button/Button';
import { SearchInput } from '@design-system/components/Inputs/SearchInput/SearchInput';
import { Filters } from '@components/Filters/Filters';
import { Checkbox, Icon, Loader } from '@design-system/index';
import { ArrowDown, ArrowUp, ExcelFull, ExcelLine, PoubelleFull, PoubelleLine } from '@icons';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { Pagination } from '@components/Pagination/Pagination';
import { UseMutation, UsePaginationMutation } from '@services/index';
import { ActiveFilter, Filter } from '@utils/filters';
import { Permission } from '@components/Permission/Permission';
import { UserRole } from '@services/core/auth/auth.types';

export type FooterAction<T> = {
    label: string;
    role?: UserRole|UserRole[];
    icon: React.ReactNode;
    hoverIcon: React.ReactNode;
    onClick: (lines: T[]) => void;
};

export type Deleting = {
    enabled: boolean;
    entity: string;
};

type TableProps<T> = {
    mutationHook: UsePaginationMutation<T>;
    exportMutationHook?: UseMutation<string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationHookParams?: any;
    deleting?: Deleting;
    footer?: {
        actions: FooterAction<T>[];
    };
    columns: ColumnDef<T>[];
    title?: string;
    className?: string;
    pagination?: boolean;
    selectable?: boolean;
    exportable?: boolean;
    searchable?: boolean;
    linePerPage?: number[];
    forceLoading?: boolean;
    onRowClick?: (row: T) => void;
    headerIcon?: React.ReactNode;
    headerIconColor?: 'primary' | 'secondary' | 'green-water' | 'green' | 'apple-green' | 'turquoise' | 'sky' | 'grey1' | 'error';
    headerTitle?: string;
    noHeader?: boolean;
    onMetadata?: (metadata: PaginationMetadata) => void;
    defaultSorting?: OrderBy;
    needReload?: boolean;
    filters?: Filter[];
    createButtonOnClick?: () => void;
    createButtonLabel?: string;
    archiveButtonOnClick?: () => void;
    archiveButtonLabel?: string;
    /** Conserve recherche, page, tri et lignes par page dans l'URL et la session (retour depuis une fiche). */
    persistKey?: string;
};

export const MutationTable = <T,>({
    mutationHook,
    exportMutationHook,
    mutationHookParams,
    columns,
    title,
    className,
    pagination = true,
    exportable = false,
    searchable = true,
    selectable = true,
    headerIcon,
    headerIconColor = 'primary',
    noHeader = false,
    forceLoading = false,
    linePerPage = [10, 20, 50, 100],
    defaultSorting,
    headerTitle,
    onMetadata,
    onRowClick,
    needReload,
    footer,
    createButtonOnClick,
    createButtonLabel,
    archiveButtonOnClick,
    archiveButtonLabel,
    filters,
    deleting,
    persistKey
}: TableProps<T>): JSX.Element => {
    const {
        data,
        isLoading,
        error,
        refresh,
        setSearch,
        initialSearch,
        isLinesPerPageModalOpen,
        setIsLinesPerPageModalOpen,
        setLinesPerPage,
        searchIsLoading,
        setCurrentPage,
        orderBy,
        setOrderBy,
        setActiveFilters,
        handleExport,
        isExportLoading,
        table,
        handleDelete
    } = useMutationTableVM<T>({
        mutationHook,
        exportMutationHook,
        mutationHookParams,
        columns,
        defaultSorting,
        deleting,
        persistKey
    });

    useEffect(() => {
        if (needReload !== undefined) {
            refresh();
        }
    }, [needReload]);

    useEffect(() => {
        if (data?.metadata && onMetadata) {
            onMetadata(data.metadata);
        }
    }, [data?.metadata, onMetadata]);

    const handleSort = (field: string) => {
        if (orderBy?.field === field) {
            setOrderBy({
                field,
                direction: orderBy.direction === 'asc' ? 'desc' : 'asc'
            });
        } else {
            setOrderBy({
                field,
                direction: 'asc'
            });
        }
    };

    const getSortIcon = (field: string) => {
        if (orderBy?.field !== field) {
            return null;
        }
        return orderBy.direction === 'asc' ? <ArrowUp /> : <ArrowDown />;
    };

    const handleSelectAllRows = (isSelected: boolean) => {
        table.toggleAllRowsSelected(isSelected);
    };

    const areRowsSelected = table.getSelectedRowModel().rows.length > 0;
    const areAllRowsSelected = table.getIsAllRowsSelected();

    if (error) {
        return (
            <div className="table-error">
                <Typography intrinsic="h3" variant="p2" color="error">
                    Une erreur est survenue, veuillez réessayer plus tard
                </Typography>
                <Button onClick={() => window.location.reload()}>Réessayer</Button>
            </div>
        );
    }

    return (
        <Card
            title={title}
            className={`${className} card-table`}
            headerType={noHeader ? 'none' : 'full'}
            headerIcon={<Icon icon={headerIcon} size={20} color={headerIconColor ?? 'primary'} />}
            headerTitle={headerTitle}
            headerStaticAction={exportable && <Button 
                variant="icon"
                color="primary"
                isLoading={isExportLoading}
                size="small"
                disabled={isExportLoading}
                hoverIcon={<Icon icon={<ExcelFull />} size={20} color="primary" />}
                icon={<Icon icon={<ExcelLine />} size={20} color="grey1" />}
                onClick={handleExport}
            />}
        >
            {((isLoading && !searchIsLoading) || forceLoading) && <Loader className="table-loader" />}
            <div className="table">
                <div className="table-top">
                    <div className="table-top-left">
                        {searchable && <SearchInput isLoading={searchIsLoading} placeholder="Rechercher" value={initialSearch ?? ''} onValueChange={setSearch} />}
                        <Typography intrinsic="p" color="primary">
                            {!data || ((data?.metadata.totalFiltered ?? data?.metadata.total) <= 0 && 'Aucun résultat trouvé')}
                            {data &&
                                (data.metadata.totalFiltered ?? data.metadata.total) > 0 &&
                                `${data.metadata.totalFiltered ?? data.metadata.total} ${(data.metadata.totalFiltered ?? data.metadata.total) > 1 ? 'résultats' : 'résultat'}`}
                        </Typography>
                    </div>
                    <div className="table-top-right">
                        {createButtonOnClick && createButtonLabel && (
                            <Button variant="primary" size="small" onClick={() => createButtonOnClick()}>
                                {createButtonLabel}
                            </Button>
                        )}
                        {archiveButtonOnClick && archiveButtonLabel && (
                            <Button variant="secondary" size="small" onClick={() => archiveButtonOnClick()}>
                                {archiveButtonLabel}
                            </Button>
                        )}

                        {filters && <Filters filters={filters} onFiltersChange={(filter: ActiveFilter[]) => setActiveFilters(filter)} />}
                    </div>
                </div>
                <div className="table-wrapper">
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup, i) => {
                            return (
                                <tr className='cosem-table-tr' key={i}>
                                    {headerGroup.headers.map((header) => {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const meta = header.column.columnDef.meta as any;
                                        return (
                                            <Permission key={header.id} role={meta?.role}>
                                            <th
                                                id={header.id}
                                                key={header.id}
                                                style={{ width: header.column.getSize(), ...meta?.style }}
                                            >
                                                {' '}
                                                {header.isPlaceholder ? null : header.column.columnDef.enableSorting ? (
                                                    <div
                                                        className="column-header"
                                                        onClick={() =>
                                                            header.column.columnDef.enableSorting && handleSort(header.column.id)
                                                        }
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {header.column.columnDef.enableSorting && (
                                                            <Icon icon={getSortIcon(header.column.id)} size={12} color="secondary" />
                                                        )}
                                                    </div>
                                                ) : (
                                                        flexRender(header.column.columnDef.header, header.getContext())
                                                    )}
                                                </th>
                                            </Permission>
                                        );
                                    })}
                                    {deleting?.enabled && (
                                        <Permission role="admin">
                                            <th style={{textAlign: 'center', width: '100px'}}>
                                                Supprimer
                                            </th>
                                        </Permission>
                                    )}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <tr className='cosem-table-tr' key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const meta = cell.column.columnDef.meta as any;
                                        const isClickable = onRowClick && (meta?.clickable ?? false);
                                        return (
                                            <Permission key={cell.id} role={meta?.role}>
                                            <td
                                                onClick={() => isClickable && onRowClick?.(row.original)}
                                                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                                                key={cell.id}
                                            >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            </Permission>
                                        );
                                    })}
                                    {deleting?.enabled && (
                                        <Permission role="admin">
                                            <td>
                                                <div style={{display: 'flex', justifyContent: 'center'}}>
                                                    <Button
                                                        variant="icon"
                                                        color="primary"
                                                        size="small"
                                                        hoverIcon={<Icon icon={<PoubelleFull />} size={20} color="primary" />}
                                                        icon={<Icon icon={<PoubelleLine />} size={20} color="primary" />}
                                                        onClick={() => {handleDelete(row.original as T & { id: string|number })}}
                                                    />
                                                </div>
                                            </td>
                                        </Permission>
                                    )}
                                </tr>
                            );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="table-bottom">
                    <div className="lines-selector" onClick={() => setIsLinesPerPageModalOpen(!isLinesPerPageModalOpen)}>
                        <Typography flex intrinsic="p" variant="p2" color="grey1">
                            Nombre de lignes ({data?.metadata.limit ?? 10}) <Icon icon={<ArrowDown />} size={10} color="grey1" />
                        </Typography>
                        {isLinesPerPageModalOpen && (
                            <Card headerType="none" className="card-modal">
                                {linePerPage.map((value, index) => (
                                    <ul key={index}>
                                        <li onClick={() => setLinesPerPage(value)}>{value}</li>
                                    </ul>
                                ))}
                            </Card>
                        )}
                    </div>
                    {pagination && data && data.metadata && (
                        <Pagination
                            totalPages={data.metadata.totalPages ?? 0}
                            currentPage={data.metadata.currentPage ?? 1}
                            onPageChange={setCurrentPage}
                        />
                    )}
                    <div className="actions-selector">
                        {selectable && (
                            <div className="actions-selector-checkbox">
                                <Checkbox checked={areAllRowsSelected} onChange={(checked) => handleSelectAllRows(checked)}>
                                    <Typography intrinsic="p" variant="p2" color="grey1">
                                        Tout sélectionner ({data?.results?.length || 0})
                                    </Typography>
                                </Checkbox>
                            </div>
                        )}
                        {footer &&
                            (footer?.actions ?? []).map((action, index) => (
                                <Permission key={index} role={action.role}>
                                    <Button
                                        key={index}
                                        variant="tertiary"
                                        color="grey-primary"
                                        size="xsmall"
                                        noTransition
                                        icon={action.icon}
                                        hoverIcon={action.hoverIcon}
                                        disabled={!areRowsSelected}
                                        onClick={() => {
                                            action.onClick(table.getSelectedRowModel().rows.map((row) => row.original));
                                        }}
                                    >
                                        {action.label}
                                    </Button>
                                </Permission>
                            ))}
                    </div>
                </div>
            </div>
        </Card>
    );
};
